import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import swal from "sweetalert";
import { useHistory } from "react-router-dom";

import makeAnimated from "react-select/animated";

import commoncss from "../../css/common.module.css";
import { ReactComponent as PlusIcon } from "../../assets/icons/AddPlus.svg";
import { ReactComponent as CirclePlus } from "../../assets/icons/circlePlus.svg";
import { ReactComponent as AddEditor } from "../../assets/icons/addEditor.svg";
import { ReactComponent as AddTable } from "../../assets/icons/addTable.svg";
import libcss from "../../css/library.module.css";
import MenuIcon from "../../assets/icons/Menu.png";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import {
  createCashflowApiHandler,
  createSubCashflowApiHandler,
  updateCashflowApiHandler,
  updateSubCashflowApiHandler,
  deleteCashflowApiHandler,
  deleteSubCashflowApiHandler,
} from "../../../WorkSpace/Components/ApiServices/apiHandler";

import { ReactComponent as EditIcon } from "../../../myfin_v2/assets/icons/editPri.svg";
import { ReactComponent as CloseIcon } from "../../../myfin_v2/assets/icons/close.svg";
import DeletePopup from "../../elements/DeletePopup";
import { ReactComponent as DelIcon } from "../../../myfin_v2/assets/icons/delete_red.svg";

import TemplateTableGenerator from "./TemplateTableGenerator";
import SidePanel from "./SidePanel.jsx";

import { getTemplateDetailsApiHandler } from "../../../WorkSpace/Components/ApiServices/apiHandler";

import QuillReactEditor from "../../components/QuillReactEditor";

const genId = () => Math.random().toString(36).slice(2);

const normalizeCashflowLinkCol = (col) => {
  const normalized = String(col || "").trim().toLowerCase();
  if (["cy", "current year", "currentyear"].includes(normalized)) {
    return "currentYear";
  }
  if (["py", "previous year", "previousyear"].includes(normalized)) {
    return "previousYear";
  }
  return col;
};

const buildCashflowLinkKeys = (tableId, row, col) => {
  if (!tableId || !row || !col) return [];

  const rowValue = String(row).trim();
  const normalizedCol = normalizeCashflowLinkCol(col);

  return [
    `${tableId}-${rowValue}-${col}`,
    `${tableId}-${rowValue}-${normalizedCol}`,
  ];
};

const findCashflowOwnerIdByTableId = (cashflowNotes, tableId) => {
  if (!Array.isArray(cashflowNotes) || !tableId) return null;

  for (const note of cashflowNotes) {
    const blocks = note?.data || [];
    const hasMatchingTable = blocks.some(
      (block) => block?.dataType === "table" && block?._id === tableId
    );

    if (hasMatchingTable) {
      return note?._id || null;
    }

    const subNoteOwnerId = findCashflowOwnerIdByTableId(note?.subNotes || [], tableId);
    if (subNoteOwnerId) {
      return subNoteOwnerId;
    }
  }

  return null;
};

const findCashflowTableById = (cashflowNotes, tableId) => {
  if (!Array.isArray(cashflowNotes) || !tableId) return null;

  for (const note of cashflowNotes) {
    const matchedBlock = (note?.data || []).find(
      (block) => block?.dataType === "table" && block?._id === tableId
    );

    if (matchedBlock) {
      return matchedBlock;
    }

    const subNoteMatch = findCashflowTableById(note?.subNotes || [], tableId);
    if (subNoteMatch) {
      return subNoteMatch;
    }
  }

  return null;
};

// Normalize your BS Note3 schema into a flat array of items
const normalizeFromInitial = (initialDoc) => {
  const items = [];
  (initialDoc?.data || []).forEach((block) => {
    if (block.dataType === "text") {
      items.push({
        id: block._id || genId(),
        _id: block._id,
        type: "text",
        dataType: "text",
        text: Array.isArray(block.text)
          ? block.text.map((t) => ({ ...t, id: t.id || genId() }))
          : [
              {
                id: genId(),
                content: block?.textRaw || block?.text?.[0]?.content || " ",
              },
            ],
      });
    } else if (block.dataType === "table") {
      items.push({
        id: block._id || genId(),
        _id: block._id,
        type: "table",
        tableTitle: block.tableTitle || "Untitled Table",
        tableRow: Array.isArray(block.tableRow)
          ? block.tableRow.map((r) => ({ ...r }))
          : [],
        tableColumn: Array.isArray(block.tableColumn)
          ? block.tableColumn.map((c) => ({ ...c }))
          : [],
        tableType: block?.tableType ?? "table",
        tableData: Array.isArray(block?.table)
          ? block.table.map((row) => ({
              ...row,
              isSubRow: row.isSubRow || row.issubrow || false,
            }))
          : null,
        hasCellData: block?.tableType === "content",
      });
    }
  });
  return items;
};

// Serialize the flat list back to your exact schema
const serialize = (name, items) => {
  const data = items.map((it) => {
    if (it.type === "text") {
      return {
        dataType: "text",
        text: (it.text || []).map((t) => ({
          content: t.content || "",
        })),
      };
    }
    if (it.type === "table") {
      const hasCellData =
        it.hasCellData !== undefined
          ? it.hasCellData
          : (it.tableType === "content" || (Array.isArray(it.tableData) && it.tableData.length > 0) || (Array.isArray(it.table) && it.table.length > 0));

      const tableObj = {
        dataType: "table",
        tableTitle: it.tableTitle || "Untitled Table",
        tableRow: (it.tableRow || []).map((r) => ({
  name: r.name || "",
  lineType: r.lineType || "line item",

  ...(r._id && { _id: r._id }),

  ...(r.formulaType && {
    formulaType: r.formulaType,
  }),

  ...(r.isBold !== undefined && {
    isBold: r.isBold,
  }),

  ...(r.lineType === "formula" && {
    formula: r.formula || [],
  }),
})),
        tableColumn: (it.tableColumn || []).map((c) => ({
          name: c.name || "",
          lineType: c.lineType || "line item",
          ...(c.lineType === "formula" && { formula: c.formula || [] }),
        })),
      };

      if (hasCellData) {
        tableObj.tableType = "content";
        tableObj.table = (it.tableData || it.table || []).map(
        ({ id, isNewRow, ...row }) => ({
          ...(row._id && { _id: row._id }),
          ...row,
        })
      );
      }

      return tableObj;
    }
    return it;
  });

  return removeIdsAndFormulas({ name, data });
};

const removeIdsAndFormulas = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => removeIdsAndFormulas(item));
  }

  if (typeof obj === "object" && obj !== null) {
    const newObj = {};

    Object.keys(obj).forEach((key) => {
      const lowerKey = key.toLowerCase();
      if (
        lowerKey === "formula" ||
        lowerKey === "isnewrow" ||
        lowerKey === "isnew"
      ) {
        return;
      }

      newObj[key] = removeIdsAndFormulas(obj[key]);
    });

    return newObj;
  }

  return obj;
};

export default function Cashflow({
  tempId,
  setHandleSave,
  handleSave,
  isReadOnly = false,
}) {
  const [name, setName] = useState("");
  const [items, setItems] = useState(() => normalizeFromInitial(null));
  const [output, setOutput] = useState(null);
  const [forceOpenTableId, setForceOpenTableId] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const animatedComponents = makeAnimated();
  let history = useHistory();
  const dispatch = useDispatch();
  const [libraryName, setLibraryName] = useState("ST Library");
  const [activeItem, setActiveItem] = useState(null);
  const [selNote, setSelNote] = useState(null);
  const [selNoteId, setSelNoteId] = useState(null);
  const [selParentId, setSelParentId] = useState(null);
  const [notes, setNotes] = useState(null);
  const [note, setNote] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleConfirmDelete = () => {
    if (!rowToDelete) return;
    const { tableId, rowIndex, rowData } = rowToDelete;
    setRowToDelete(null);

    const rowId = rowData?._id || rowData?.id || null;
    const isAddedLineItem =
      rowData?.isSubRow === true ||
      rowData?.issubrow === true;

    isDeletingRef.current = true;

    if (isAddedLineItem && !rowId) {
      // Unsaved / local row - delete locally
      setItems((prev) =>
        prev.map((it) => {
          if (it.id === tableId) {
            const nextTableData = [...(it.tableData || [])];
            const nextTableRow = [...(it.tableRow || [])];
            nextTableData.splice(rowIndex - 1, 1);
            nextTableRow.splice(rowIndex - 1, 1);
            return {
              ...it,
              tableData: nextTableData,
              tableRow: nextTableRow,
            };
          }
          return it;
        })
      );
      swal("Your line item has been deleted!", {
        icon: "success",
      });
      return;
    }

    const apiCall = isAddedLineItem
      ? deleteSubCashflowApiHandler({
          groupingTemplateID: tempId,
          cashflowID: selNoteId,
          subNoteID: rowId,
        })
      : deleteCashflowApiHandler({
          groupingTemplateID: tempId,
          cashflowID: rowId || tableId,
        });

    apiCall
      .then((response) => {
        if (response?.data?.success) {
          getTemplateDetails();
          swal("Your line item has been deleted!", {
            icon: "success",
          });
        } else {
          swal(
            "Error",
            response?.data?.error || "Unable to delete the row",
            "error"
          );
        }
      })
      .catch((error) => {
        swal(
          "Error",
          error?.response?.data?.error || "Unable to delete the row",
          "error"
        );
      });
  };

  const [isComposing, setIsComposing] = useState(false);
  const [noteName, setNoteName] = useState("");
  const inputRef = useRef(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingSubIndex, setEditingSubIndex] = useState(null);
  const [editValue, setEditValue] = useState("");
  const isDeletingRef = useRef(false);

  const [showPanel, setShowPanel] = useState(false);
  const [activeCellKey, setActiveCellKey] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [linkedCells, setLinkedCells] = useState({});
  const [activeTableId, setActiveTableId] = useState(null);
  const [activeRawTableId, setActiveRawTableId] = useState(null);

const resolvedCashflowId = selNoteId;

const activeTable = findCashflowTableById(templateData?.cashflow, activeTableId);

const onCellLinkClick = (tableId, r, c, tableRow) => {
  const rowName = tableRow[r - 1]?.name || String(r);
  const colHeader = c === 1 ? "currentYear" : "previousYear";

  setSelectedCell({ row: rowName, col: colHeader });
  setActiveTableId(tableId);
  setActiveRawTableId(tableId);
  setActiveCellKey(`${tableId}-${r}-${c}`);
  setShowPanel(true);
};

  const handleApply = (data, updatedTemplate) => {
    setLinkedCells((prev) => {
      const updated = { ...prev };
      if (!data) {
        delete updated[activeCellKey];
      } else {
        updated[activeCellKey] = data;
      }
      return updated;
    });

    if (updatedTemplate) {
      setTemplateData(updatedTemplate);
    } else {
      getTemplateDetails();
    }
  };

  const getTemplateDetails = (updatedTemplate) => {
    if (updatedTemplate && updatedTemplate.cashflow) {
      setTemplateData(updatedTemplate);
      return;
    }
    getTemplateDetailsApiHandler({ groupingTemplateID: tempId })
      .then((response) => {
        if (response?.data?.success === true) {
          setTemplateData(response?.data?.groupingTemplate);
        }
      })
      .catch((error) => {});
  };

  useEffect(() => {
    getTemplateDetails();
  }, []);

  useEffect(() => {
    getTemplateDetails();
  }, [activeItem]);

 useEffect(() => {
  if (!showPanel) return;
  if (activeTableId) return;
  setShowPanel(false);
  swal("Error", "Table ID is missing.", "error");
}, [showPanel, activeTableId]);

  useEffect(() => {
    if (notes && notes.length > 0 && !selNote) {
      const firstNote = notes[0];
      setItems(normalizeFromInitial(firstNote));
      setActiveItem(`gs_0`);
      setSelNote("general");
      setSelNoteId(firstNote._id);
      setSelParentId(null);
      setOutput(firstNote);
      setName(firstNote.name);
    }
  }, [notes, selNote]);

  const [isAddingTable, setIsAddingTable] = useState(false);

  const getResolvedTableId = (item) => item?._id || item?.id;

  const handleAddParagraph = () => {
    setItems((prev) => [
      ...prev,
      {
        id: genId(),
        type: "text",
        text: [{ id: genId(), content: "" }],
        textFormat: "html",
        textRaw: " ",
        textPlain: " ",
      },
    ]);
  };

  const handleAddTable = () => {
    setIsAddingTable(true);
  };

  const removeItem = (id) => {
    const target = items.find((it) => it.id === id);
    if (!target) return;

    if (!templateData?._id || !selNoteId) {
      swal("Error", "Template data not loaded yet", "error");
      return;
    }

    setDeleteConfirm({
      title: "Are you sure?",
      message: "This item will be deleted permanently",
      onConfirm: () => {
        setDeleteConfirm(null);
        const filteredItems = items.filter((it) => it.id !== id);
        const payload = serialize(name, filteredItems);

        const apiCall =
          selNote === "general_sn"
            ? updateSubCashflowApiHandler(
                {
                  groupingTemplateID: templateData._id,
                  cashflowID: selParentId,
                  subNoteID: selNoteId,
                },
                payload
              )
            : updateCashflowApiHandler(
                { groupingTemplateID: templateData._id, cashflowID: selNoteId },
                payload
              );

        apiCall
          .then((response) => {
            if (response?.data?.success === true) {
              setItems(filteredItems);
              swal("Successfully deleted", "", "success");
              getTemplateDetails();
            } else {
              swal(
                "Error",
                response?.data?.error || "Unable to delete the item",
                "error"
              );
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error ||
                error?.message ||
                "Unable to delete the item",
              "error"
            );
          });
      }
    });
  };

 const updateEditorContent = (id, html) => {
  setItems((prev) =>
    prev.map((it) =>
      it.id === id
        ? {
            ...it,
            text: [{
              id: it.text?.[0]?.id ?? genId(),
              content: html?.html || ""
            }],
            textFormat: "html",
            textRaw: html?.html ?? " ",
            textPlain: html?.text ?? " ",
          }
        : it
    )
  );
};
  const lastChangedRowInfoRef = useRef(null);

  const updateTableItem = (id, patch, rowIndex) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.id === id) {
          const next = { ...it, ...patch };
          if (
            rowIndex != null &&
            next.tableData &&
            next.tableData[rowIndex - 1]
          ) {
            next.tableData = [...next.tableData];
            next.tableData[rowIndex - 1] = {
              ...next.tableData[rowIndex - 1],
              isEdited: true,
            };
            lastChangedRowInfoRef.current = {
              tableId: id,
              rowIndex,
              rowData: next.tableData[rowIndex - 1],
            };
          }
          return next;
        }
        return it;
      })
    );
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);
    setItems(newItems);
  };

  useEffect(() => {
    setOutput(serialize(name, items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, items]);

  useEffect(() => {
    if (isComposing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isComposing]);

  const handleAddClick = () => {
    setIsComposing(true);
    setNoteName("");
  };

  const handleCancel = () => {
    setIsComposing(false);
    setNoteName("");
  };

  const handlePost = () => {
    const value = noteName.trim();
    if (!value) return;
    setNote([value, ...note]);
    setNoteName("");
    setIsComposing(false);
  };

  const handleUpdateNote = (id) => {
    updateCashflowApiHandler(
      { groupingTemplateID: tempId, cashflowID: id },
      { name: editValue }
    )
      .then((res) => {
        if (res?.data?.success) {
          setEditingIndex(null);
          getTemplateDetails();
        }
      })
      .catch(() => {});
  };

  const handleUpdateSubNote = (parentId, subId) => {
    updateSubCashflowApiHandler(
      {
        groupingTemplateID: tempId,
        cashflowID: parentId,
        subNoteID: subId,
      },
      { name: editValue }
    )
      .then((res) => {
        if (res?.data?.success) {
          setEditingSubIndex(null);
          getTemplateDetails();
        }
      })
      .catch(() => {});
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handlePost();
    if (e.key === "Escape") handleCancel();
  };

  const [subComposeFor, setSubComposeFor] = useState(null);
  const [subNoteName, setSubNoteName] = useState("");
  const subInputRef = useRef(null);

  useEffect(() => {
    if (subComposeFor !== null && subInputRef.current) {
      subInputRef.current.focus();
    }
  }, [subComposeFor]);

  const openSubComposer = (noteIndex) => {
    setSubComposeFor(noteIndex);
    setSubNoteName("");
  };

  const cancelSubComposer = () => {
    setSubComposeFor(null);
    setSubNoteName("");
  };

  const postSubnote = () => {
    const value = subNoteName.trim();
    if (!value || subComposeFor === null) return;
    setNotes((prev) => {
      const next = [...prev];
      const target = { ...next[subComposeFor] };
      const subNotes = Array.isArray(target.subNotes)
        ? [...target.subNotes]
        : [];
      subNotes.push({ name: value });
      target.subNotes = subNotes;
      next[subComposeFor] = target;
      return next;
    });
    setSubNoteName("");
    setSubComposeFor(null);
  };

  const addSub = (note_id) => {
    const value = subNoteName.trim();
    if (!value) return;

    const generalNotesPayload = { name: subNoteName };
    createSubCashflowApiHandler(
      { groupingTemplateID: tempId, cashflowID: note_id },
      generalNotesPayload
    )
      .then((response) => {
        if (response?.data?.success === true) {
          setSubNoteName("");
          setSubComposeFor(null);
          setNotes(response?.data?.updatedGroupingTemplate?.cashflow);
        }
      })
      .catch((error) => {});
  };

  const handleSubKeyDown = (e) => {
    if (e.key === "Enter") postSubnote();
    if (e.key === "Escape") cancelSubComposer();
  };

  // ─────────────────────────────────────────────────────────────────
  // SAVE handler
  // ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (handleSave === "cashflow") {
      
      setHandleSave(null);

      const newRowsToCreate = [];
      items.forEach((it) => {
        if (it.type === "table" && it.tableData) {
          const cols = it.tableColumn || [];
          const col1Name = cols[0]?.name || "Particulars";
          const col2Name = cols[1]?.name || "Amount";
          const col3Name = cols[2]?.name || "";

          it.tableData.forEach((row) => {
            if (row.isNewRow === true) {
              const nameVal = row[col1Name] || row.name || "";
              const cyVal = row[col2Name] || row.currentYear || 0;
              const pyVal = col3Name
                ? row[col3Name] || row.previousYear || 0
                : row.previousYear || 0;

              const cleanCy = Number(String(cyVal).replace(/,/g, "")) || 0;
              const cleanPy = Number(String(pyVal).replace(/,/g, "")) || 0;

              newRowsToCreate.push({
                tableId: it.id,
                payload: {
                  name: nameVal,
                  currentYear: cleanCy,
                  previousYear: cleanPy,
                  isSubRow: true,
                },
              });
            }
          });
        }
      });

      const finalizeSave = (freshPayload) => {
        const payload = freshPayload || removeIdsAndFormulas(output);

        if (selNote === "general_sn") {
          updateSubCashflowApiHandler(
            {
              groupingTemplateID: templateData._id,
              cashflowID: selParentId,
              subNoteID: selNoteId,
            },
            payload
          )
            .then((response) => {
              if (response?.data?.success === true) {
                swal("Data saved successfully", "", "success");
                getTemplateDetails();
              } else {
                swal(
                  "Error",
                  response?.data?.error || "Unable to save the data",
                  "error"
                );
              }
            })
            .catch((error) => {
              swal(
                "Error",
                error?.response?.data?.error || "Unable to save the data",
                "error"
              );
            });
        } else {
          updateCashflowApiHandler(
            { groupingTemplateID: templateData._id, cashflowID: selNoteId },
            payload
          )
            .then((response) => {
              if (response?.data?.success === true) {
                swal("Data saved successfully", "", "success");
                getTemplateDetails();
              } else {
                swal(
                  "Error",
                  response?.data?.error || "Unable to save the data",
                  "error"
                );
              }
            })
            .catch((error) => {
              swal(
                "Error",
                error?.response?.data?.error || "Unable to save the data",
                "error"
              );
            });
        }
      };

     if (newRowsToCreate.length > 0) {
  const createPromises = newRowsToCreate.map((item) =>
    createSubCashflowApiHandler(
      { groupingTemplateID: templateData._id, cashflowID: selNoteId },
      { name: item.payload.name || "" }
    )
  );

  Promise.all(createPromises)
    .then((responses) => {
      console.log("CREATE RESPONSES:", JSON.stringify(responses.map(r => r?.data)));

     const createdIds = [];
responses.forEach((res) => {
  const cashflow = res?.data?.updatedGroupingTemplate?.cashflow;
  if (!cashflow) return;

  for (const note of cashflow) {
    if (note._id === selNoteId) {
      const subs = note.subNotes || [];
      const existingIds = (items || [])
        .flatMap((it) => it.tableData || [])
        .map((r) => r._id)
        .filter(Boolean);
      for (let i = subs.length - 1; i >= 0; i--) {
        if (
          !existingIds.includes(subs[i]._id) &&
          !createdIds.includes(subs[i]._id)
        ) {
          createdIds.push(subs[i]._id);
          break;
        }
      }
      break;
    }
  }
});

      let createdIndex = 0;

      const updatedItems = items.map((it) => {
        if (it.type !== "table") return it;

        return {
          ...it,
          tableData: (it.tableData || []).map((row) => {
            if (
              row.isNewRow === true &&
              createdIndex < createdIds.length
            ) {
              const realId = createdIds[createdIndex++];

              return {
                ...row,
                _id: realId,
                id: realId,
                isNewRow: false,
              };
            }

            return row;
          }),
        };
      });

      setItems(updatedItems);

      const freshOutput = serialize(name, updatedItems);
      finalizeSave(removeIdsAndFormulas(freshOutput));
      })
      .catch(() => {
        swal(
          "Error",
          "Failed to create new line items. Please try again.",
          "error"
        );
      });
  } else {
    finalizeSave();
  }
    }
  }, [handleSave, output, selNote, selNoteId, selParentId, templateData, items, setHandleSave]);

  // ─────────────────────────────────────────────────────────────────
  // AUTO-SAVE handler
  // ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!output || !selNoteId || !templateData) return;

    let hasEmptyNewRow = false;
    (items || []).forEach((it) => {
      if (it.type === "table") {
        if (it.tableData) {
          it.tableData.forEach((row) => {
            const cols = it.tableColumn || [];
            const col1Name = cols[0]?.name || "Particulars";
            const nameVal = row[col1Name] || row.name || "";
            if (row.isNewRow === true && String(nameVal).trim() === "") {
              hasEmptyNewRow = true;
            }
          });
        }
        if (it.tableRow) {
          it.tableRow.forEach((row) => {
            if (row.isNewRow === true && String(row.name || "").trim() === "") {
              hasEmptyNewRow = true;
            }
          });
        }
      }
    });

    if (hasEmptyNewRow) return;

    const timer = setTimeout(() => {
      if (isDeletingRef.current) {
        isDeletingRef.current = false;
        return;
      }

      const payload = removeIdsAndFormulas(output);
      const info = lastChangedRowInfoRef.current;
      const hasNewRow = (items || []).some(
        (it) =>
          it.type === "table" &&
          ((it.tableData && it.tableData.some((row) => row.isNewRow === true)) ||
            (it.tableRow && it.tableRow.some((row) => row.isNewRow === true)))
      );

      if (selNote === "general_sn") {
        updateSubCashflowApiHandler(
          {
            groupingTemplateID: tempId,
            cashflowID: selParentId,
            subNoteID: selNoteId,
          },
          payload
        )
          .then((res) => {
            if (hasNewRow && res?.data?.success) {
              getTemplateDetails();
            }
          })
          .catch((err) => console.error("Auto-save error", err));
      } else if (
        info?.rowData?._id &&
        (info?.rowData?.isSubRow || info?.rowData?.issubrow) &&
        info?.rowData?.isEdited === true
      ) {
        // ── Newly added sub-row: update name via updateSubCashflow ──
        const firstTable = items.find((it) => it.type === "table");
        const cols = firstTable?.tableColumn || [];
        const col1Name = cols[0]?.name || "Particulars";
        const nameVal =
          info.rowData.name || info.rowData[col1Name] || "";

        updateSubCashflowApiHandler(
          {
            groupingTemplateID: tempId,
            cashflowID: selNoteId,
            subNoteID: info.rowData._id,
          },
          { name: nameVal }
        )
          .then(() => {
            lastChangedRowInfoRef.current = null;
            setItems((prev) =>
              prev.map((it) => {
                if (it.id === info.tableId) {
                  return {
                    ...it,
                    tableData: (it.tableData || []).map((r) =>
                      r._id === info.rowData._id || r.id === info.rowData._id
                        ? { ...r, isEdited: false }
                        : r
                    ),
                  };
                }
                return it;
              })
            );
          })
          .catch((err) => console.error("Auto-save sub-row error", err));
      } else {
        updateCashflowApiHandler(
          { groupingTemplateID: tempId, cashflowID: selNoteId },
          payload
        )
          .then((res) => {
            if (hasNewRow && res?.data?.success) {
              getTemplateDetails();
            }
          })
          .catch((err) => console.error("Auto-save error", err));
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [output, selNote, selNoteId, selParentId, tempId, templateData, items]);

  useEffect(() => {
    setNotes(templateData?.cashflow);
    if (!templateData?.cashflow) return;

    if (selNoteId) {
      let activeNote = null;
      const findNote = (arr) => {
        for (const n of arr) {
          if (n._id === selNoteId) {
            activeNote = n;
            return;
          }
          if (n.subNotes) {
            findNote(n.subNotes);
          }
        }
      };
      findNote(templateData.cashflow);

      if (activeNote) {
        setItems(normalizeFromInitial(activeNote));
      }
    }

    const links = {};
    const extractLinks = (notesArray) => {
      if (!Array.isArray(notesArray)) return;
      notesArray.forEach((note) => {
        const blocks = note.data || [];
        blocks.forEach((it) => {
          if (it.dataType === "table" && it.cellFormulas) {
            it.cellFormulas.forEach((f) => {
              buildCashflowLinkKeys(it._id, f.row, f.col).forEach((key) => {
                links[key] = f;
              });
            });
          }
        });
        if (note.subNotes) {
          extractLinks(note.subNotes);
        }
      });
    };
    extractLinks(templateData.cashflow);
    setLinkedCells(links);
  }, [templateData]);

  useEffect(() => {
    if (notes && notes.length > 0 && !selNote) {
      const firstNote = notes[0];
      setItems(normalizeFromInitial(firstNote));
      setActiveItem(`gs_0`);
      setSelNote("general");
      setSelNoteId(firstNote._id);
      setSelParentId(null);
      setOutput(firstNote);
      setName(firstNote.name);
    }
  }, [notes, selNote]);

  const addNotes = () => {
    const value = noteName.trim();
    if (!value) return;

    const generalNotesPayload = { name: noteName };
    createCashflowApiHandler(
      { groupingTemplateID: tempId },
      generalNotesPayload
    )
      .then((response) => {
        if (response?.data?.success === true) {
          setNoteName("");
          setIsComposing(false);
          setNotes(response?.data?.updatedGroupingTemplate?.cashflow);
        }
      })
      .catch((error) => {
        swal(
          "Error",
          error?.response?.data?.error || "Unable to save the data",
          "error"
        );
      });
  };

  const resetItems = () => {
    setItems(normalizeFromInitial(null));
    setActiveItem(null);
    setSelNote(null);
    setSelNoteId(null);
    setSelParentId(null);
  };

  const deleteNoteHandler = (id) => {
    setDeleteConfirm({
      title: "Confirm deletion of cashflow data?",
      message: "Once deleted, you will not be able to recover this!",
      onConfirm: () => {
        setDeleteConfirm(null);
        isDeletingRef.current = true;
        deleteCashflowApiHandler({
          groupingTemplateID: tempId,
          cashflowID: id,
        })
          .then((response) => {
            if (response?.data?.success) {
              resetItems();
              getTemplateDetails();
              swal("Successfully deleted", "", "success");
            } else {
              swal(
                "Error",
                response?.data?.error || "Unable to delete",
                "error"
              );
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error || "Unable to delete",
              "error"
            );
          });
      }
    });
  };

  const deleteSubNoteHandler = (id, subid) => {
    setDeleteConfirm({
      title: "Confirm deletion of cashflow data?",
      message: "Once deleted, you will not be able to recover this!",
      onConfirm: () => {
        setDeleteConfirm(null);
        isDeletingRef.current = true;
        deleteSubCashflowApiHandler({
          groupingTemplateID: tempId,
          cashflowID: id,
          subNoteID: subid,
        })
          .then((response) => {
            if (response?.data?.success) {
              resetItems();
              getTemplateDetails();
              swal("Successfully deleted", "", "success");
            } else {
              swal(
                "Error",
                response?.data?.error || "Unable to delete",
                "error"
              );
            }
          })
          .catch((error) => {
            swal(
              "Error",
              error?.response?.data?.error || "Unable to delete",
              "error"
            );
          });
      }
    });
  };

  const handleDeleteTableRow = (tableId, rowIndex, rowData) => {
    setRowToDelete({ tableId, rowIndex, rowData });
  };

 const handleAddSubRow = (tableId) => {
  if (!selNoteId) return;

 const tableItem = items.find((it) => it.id === tableId);
  const columns = tableItem?.tableColumn || [];

  const hasCellData =
    tableItem?.hasCellData !== undefined
      ? tableItem.hasCellData
      : (tableItem?.tableType === "content" || (Array.isArray(tableItem?.tableData) && tableItem.tableData.length > 0));

  const newRow = {
    isNewRow: true,
    isEdited: false,
    isSubRow: true,
  };

  if (columns.length > 0) {
    columns.forEach((col, idx) => {
      const key = col.name;
      if (key) {
        newRow[key] = idx === 0 ? "" : "0";
      }
    });
  } else {
    newRow["Particulars"] = "";
    newRow["Amount"] = "0";
  }

  setItems((prev) =>
    prev.map((it) => {
      if (it.id === tableId) {
        if (hasCellData) {
          return {
            ...it,
            tableData: [...(it.tableData || []), newRow],
            tableRow: [
              ...(it.tableRow || []),
              {
                name: "",
                lineType: "line item",
                isSubRow: true,
                isNewRow: true,
                isEdited: false,
              },
            ],
          };
        } else {
          return {
            ...it,
            tableRow: [
              ...(it.tableRow || []),
              {
                name: "",
                lineType: "line item",
                isSubRow: true,
                isNewRow: true,
                isEdited: false,
              },
            ],
          };
        }
      }
      return it;
    })
  );
};

  const getRowPermissions = (rowLabel) => ({
    allowEdit: !isReadOnly,
    allowDelete: !isReadOnly,
    allowRearrange: !isReadOnly,
  });

  return (
    <>
      <motion.div
        initial={{ x: 500, y: 500, opacity: 0 }}
        animate={{ x: 0, y: 0, opacity: 1 }}
        exit={{ x: 0, y: 0, opacity: 0 }}
        transition={{ duration: 0.7, ease: "easeInOut" }}
        className={libcss.hierarchyLayout}
        style={{
          border: "0px",
          marginTop: "0px",
          height: "calc(100% - 45px)",
          gap: "10px",
          width: "100%",
          maxWidth: "100%",
          paddingLeft: "20px",
          paddingRight: "20px",
          paddingTop: "10px",
          margin: 0,
        }}
      >
        <div
          className={libcss.dataDivs}
          style={{
            width: "100%",
            border: "none",
            background: "none",
            padding: "0px 0px 8px 0px",
            height: "100%",
            overflowY: "auto",
          }}
        >
          <div className={libcss.levelCatDiv} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="right-panel-items">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ display: "grid", gap: 20 }}
                  >
                    {items.map((it, idx) => (
                      <Draggable
                        key={it.id}
                        draggableId={it.id}
                        index={idx}
                        isDragDisabled={isReadOnly}
                      >
                        {(dragProvided) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            style={{
                              ...dragProvided.draggableProps.style,
                              width: "100%",
                              minWidth: "1000px",
                            }}
                          >
                            {/* Table header */}
                            {it.type === "table" && (
                              <div
                                style={{
                                  background: "#f8f9f9",
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "10px 14px",
                                  border: "1px solid #e3efe7",
                                  borderCollapse: "collapse",
                                  borderBottom: "none",
                                  borderRadius: "10px 10px 0 0",
                                  gap: "12px",
                                }}
                              >
                                <div
                                  {...dragProvided.dragHandleProps}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={MenuIcon}
                                    alt="drag"
                                    style={{ cursor: "grab", width: 9 }}
                                  />
                                </div>
                                <span
                                  style={{
                                    fontWeight: 700,
                                    fontSize: "14px",
                                    color: "#205a46",
                                    textTransform: "uppercase",
                                    flex: 1,
                                  }}
                                >
                                  {it.tableTitle || "Table"}
                                </span>
                                {!isReadOnly && (
                                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                    <EditIcon
                                      style={{ cursor: "pointer", width: 16, height: 16 }}
                                      onClick={() => setForceOpenTableId(it.id)}
                                    />
                                    <DelIcon
                                      style={{
                                        cursor: "pointer",
                                        width: 16,
                                        height: 16,
                                      }}
                                      onClick={() => removeItem(it.id)}
                                    />
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Content */}
                            {it.type === "text" ? (
                              <QuillReactEditor
                                value={it.text?.[0]?.content ?? " "}
                                onChange={(content) => {
                                  updateEditorContent(it.id, content);
                                }}
                                disabled={isReadOnly}
                                dragHandleProps={dragProvided.dragHandleProps}
                                rightControl={
                                  !isReadOnly && (
                                    <DelIcon
                                      style={{
                                        cursor: "pointer",
                                        width: 16,
                                        height: 16,
                                        marginRight: 8,
                                      }}
                                      onClick={() => removeItem(it.id)}
                                    />
                                  )
                                }
                              />
                            ) : (
                              <div
                                style={{
                                  border: "1px solid #e3efe7",
                                  borderRadius: "0 0 10px 10px",
                                  background: "#fff",
                                  padding: 0,
                                  position: "relative",
                                }}
                              >
                                <TemplateTableGenerator
                                  isCashflowTable={true}
                                  tableId={getResolvedTableId(it)}
                                  getRowPermissions={getRowPermissions}
                                  onDeleteRow={(rowIndex, rowData) =>
                                    handleDeleteTableRow(
                                      getResolvedTableId(it),
                                      rowIndex,
                                      rowData
                                    )
                                  }
                                  onAddRow={() => handleAddSubRow(getResolvedTableId(it))}
                                  onCellLinkClick={(r, c, rows) =>
                                    onCellLinkClick(getResolvedTableId(it), r, c, rows)
                                  }
                                  linkedCells={linkedCells}
                                  forceOpen={getResolvedTableId(it) === forceOpenTableId}
                                  // Pass showDeleteColumn so TemplateTableGenerator
                                  // renders an extra dedicated Delete column
                                  showDeleteColumn={!isReadOnly}
                                  value={{
                                    tableTitle: it.tableTitle,
                                    tableRow: it.tableRow,
                                    tableColumn: it.tableColumn,
                                    tableType: it.tableType,
                                    table: it.tableData,
                                    hasCellData: it.hasCellData,
                                  }}
                                  onCellBlur={(r, c, val) => {
                                    // Trigger auto-save when a cell is blurred (or enter pressed)
                                    setHandleSave("cashflow");
                                  }}
                                  onChange={(next, rowIndex) => {
                                    updateTableItem(
                                      it.id,
                                      {
                                        tableTitle: next.tableTitle,
                                        tableRow: next.tableRow,
                                        tableColumn: next.tableColumn,
                                        hasCellData: next.hasCellData,
                                        tableData: next.tableData,
                                        tableType: next.hasCellData
                                          ? "content"
                                          : "table",
                                      },
                                      rowIndex
                                    );
                                  }}
                                  onAfterOpen={() => {
                                    if (forceOpenTableId === getResolvedTableId(it))
                                      setForceOpenTableId(null);
                                  }}
                                  disabled={isReadOnly}
                                  hideHeader={true}
                                  allowReorder={true}
                                  allowAddRow={true}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          {selNote && !isReadOnly && (
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                padding: 12,
                borderRadius: 12,
                marginBottom: 12,
              }}
            >
              <CirclePlus width={37} />
              <AddEditor width={140} onClick={handleAddParagraph} />
              <AddTable width={100} onClick={handleAddTable} />
            </div>
          )}

          {isAddingTable && (
            <TemplateTableGenerator
              forceOpen={true}
              value={{}}
              onChange={(next) => {
                const id = genId();
                setItems((prev) => [
                  ...prev,
                  {
                    id,
                    type: "table",
                    tableTitle: next.tableTitle,
                    tableRow: next.tableRow,
                    tableColumn: next.tableColumn,
                    hasCellData: next.hasCellData,
                    tableData: next.tableData,
                    tableType: "",
                    table: [],
                  },
                ]);
                setIsAddingTable(false);
              }}
              onCancel={() => setIsAddingTable(false)}
            />
          )}
        </div>
      </motion.div>

     <SidePanel
      type="cashflow"
      activeCellKey={activeCellKey}
      isTemplate={true}
      showLinkPanel={showPanel}
      setShowLinkPanel={setShowPanel}
      onApply={handleApply}
      groupingTemplateID={tempId}
      selectedCell={selectedCell}
      cashflowID={resolvedCashflowId}   
      dataID={resolvedCashflowId}           
      table={activeTable}
    />

      <DeletePopup
        isOpen={rowToDelete !== null}
        title="Are you sure?"
        message="Once deleted, you will not be able to recover this line item!"
        onConfirm={handleConfirmDelete}
        onCancel={() => setRowToDelete(null)}
      />
      <DeletePopup
        isOpen={deleteConfirm !== null}
        title={deleteConfirm?.title}
        message={deleteConfirm?.message}
        onConfirm={deleteConfirm?.onConfirm}
        onCancel={() => setDeleteConfirm(null)}
      />
    </>
  );
}
